import React from 'react';
import { Card, Progress, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DASH } from '../bq-insights/bqMath';
import { progress, targetStatus } from './monetizationMath';
import type { TargetStatus } from './monetizationMath';
import type { Target } from './targets';

const { Text } = Typography;

const STATUS: Record<TargetStatus, { label: string; color: string }> = {
  met: { label: 'Target met', color: 'green' },
  'on-track': { label: 'On track', color: 'blue' },
  behind: { label: 'Behind', color: 'orange' },
  'no-data': { label: 'No data', color: 'default' },
};

export interface TargetTileProps {
  id: string;
  label: string;
  actual: number | null;
  target: Target;
  /** Formats a value for the tile (the actual, the target and the baseline). */
  format: (v: number) => string;
  /** Text of the (i) tooltip next to the label. */
  info?: string;
}

/** One KPI against its 15 Dec 2026 target: value, target and baseline, status tag, progress bar. */
export const TargetTile: React.FC<TargetTileProps> = ({
  id,
  label,
  actual,
  target,
  format,
  info,
}) => {
  const status = targetStatus(actual, target);
  const p = progress(actual, target);
  const { label: statusLabel, color } = STATUS[status];
  const direction = target.higherIsBetter ? '' : ' (lower is better)';
  return (
    <Card size="small" data-tile={id} data-status={status}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          <span>{label}</span>
          {info && (
            <Tooltip title={info}>
              <InfoCircleOutlined style={{ marginLeft: 6 }} data-info={id} aria-label={info} />
            </Tooltip>
          )}
        </Text>
        <Tag color={color} style={{ marginInlineEnd: 0 }}>
          {statusLabel}
        </Tag>
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.3 }} data-value={id}>
        {actual === null ? DASH : format(actual)}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }} data-target-line={id}>
        {`Target ${format(target.target)}${direction}`}
        {target.baseline !== null && ` · baseline ${format(target.baseline)}`}
      </Text>
      {p !== null && (
        <div data-progress={id}>
          <Progress
            percent={Math.round(p * 100)}
            size="small"
            showInfo={false}
            status={status === 'met' ? 'success' : 'normal'}
          />
        </div>
      )}
    </Card>
  );
};
